import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';
import { SentimentPill } from '@/components/CommunityPostBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StockModalCommunitySurfacer — Round 83 (Feb 14, 2026)
 * Renders inside the StockModal as a compact "💬 talk to your stocks"
 * card. Hidden when there are zero posts about the ticker (we don't
 * want to advertise emptiness). One click jumps to the filtered feed.
 */
export default function StockModalCommunitySurfacer({ ticker, onNavigate }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/community/stock-surfacer/${ticker}`, { params: { days: 30 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  if (loading) return null;
  if (!data || !data.total_posts) return null;

  const overall = data.bullish_pct > data.bearish_pct
    ? 'bullish'
    : data.bearish_pct > data.bullish_pct
    ? 'bearish'
    : 'neutral';
  const cfg = {
    bullish: { Icon: TrendingUp, cls: 'text-emerald-600 dark:text-emerald-400' },
    bearish: { Icon: TrendingDown, cls: 'text-rose-600 dark:text-rose-400' },
    neutral: { Icon: Minus, cls: 'text-zinc-500' },
  }[overall];

  const handleOpen = () => {
    const target = `/community?tab=feed&ticker=${encodeURIComponent(ticker)}`;
    if (onNavigate) onNavigate(target);
    else navigate(target);
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      className="w-full text-left rounded-xl border border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50/60 to-card dark:from-violet-950/30 dark:to-card p-3 hover:border-violet-400 dark:hover:border-violet-600 transition group"
      data-testid={`community-surfacer-${ticker}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center flex-shrink-0">
          <MessageCircle className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold">
            <span className="font-mono">{ticker}</span> · {data.total_posts}{' '}
            community {data.total_posts === 1 ? 'post' : 'posts'} in the last {data.days} days
          </p>
          <div className="flex items-center gap-2 mt-1 text-xs flex-wrap">
            <span className={`inline-flex items-center gap-1 font-semibold ${cfg.cls}`}>
              <cfg.Icon className="w-3 h-3" />
              {overall === 'bullish' ? 'Mostly bullish' : overall === 'bearish' ? 'Mostly bearish' : 'Mixed'}
            </span>
            <span className="text-muted-foreground tabular-nums">
              {data.bullish_pct}% / {data.bearish_pct}%
            </span>
          </div>
          {data.preview && data.preview.length > 0 ? (
            <div className="mt-1.5 space-y-0.5">
              {data.preview.slice(0, 2).map((p) => (
                <div key={p.post_id} className="flex items-center gap-1.5 text-[11px]">
                  <SentimentPill label={p.sentiment_label} />
                  <span className="font-semibold truncate flex-1">{p.title}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <ChevronRight className="w-4 h-4 text-violet-500 group-hover:translate-x-0.5 transition flex-shrink-0 mt-1.5" />
      </div>
    </button>
  );
}
